import { useState, useEffect, useCallback } from 'react';

interface PWAHookReturn {
    isInstalled: boolean;
    isUpdateAvailable: boolean;
    isOnline: boolean;
    installPrompt: (() => Promise<void>) | null;
    updateApp: () => void;
    registerSW: () => void;
    showInstallPrompt: boolean;
}

export const usePWA = (): PWAHookReturn => {
    const [isInstalled, setIsInstalled] = useState(false);
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [installPrompt, setInstallPrompt] = useState<(() => Promise<void>) | null>(null);
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);
    const [swRegistration, setSWRegistration] = useState<ServiceWorkerRegistration | null>(null);

    useEffect(() => {
        // Check if app is installed
        const checkInstallation = () => {
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
            const isIOSInstalled = (window.navigator as any).standalone === true;
            setIsInstalled(isStandalone || isIOSInstalled);
        };

        checkInstallation();

        // Online/offline detection
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Install prompt handling
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            const deferredPrompt = e as any;

            setInstallPrompt(() => async () => {
                await deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;

                if (outcome === 'accepted') {
                    console.log('✅ PWA installation accepted');
                    setShowInstallPrompt(false);
                }

                setInstallPrompt(null);
            });

            setShowInstallPrompt(true);
        };

        // App installed event
        const handleAppInstalled = () => {
            console.log('🎉 PWA installed successfully');
            setIsInstalled(true);
            setShowInstallPrompt(false);
            setInstallPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const registerSW = useCallback(async () => {
        if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
            return;
        }

        try {
            const registration = await navigator.serviceWorker.register('/sw.js', {
                scope: '/',
            });

            setSWRegistration(registration);

            // Check for updates
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            setIsUpdateAvailable(true);
                        }
                    });
                }
            });

            // Handle messages from service worker
            navigator.serviceWorker.addEventListener('message', (event) => {
                if (event.data && event.data.type === 'SW_UPDATE_READY') {
                    setIsUpdateAvailable(true);
                }
            });

            console.log('✅ Service Worker registered successfully');
        } catch (error) {
            console.error('❌ Service Worker registration failed:', error);
        }
    }, []);

    const updateApp = useCallback(() => {
        if (swRegistration && swRegistration.waiting) {
            swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
            window.location.reload();
        }
    }, [swRegistration]);

    return {
        isInstalled,
        isUpdateAvailable,
        isOnline,
        installPrompt,
        updateApp,
        registerSW,
        showInstallPrompt,
    };
};

// PWA utility functions
export const pwaUtils = {
    // Share content using Web Share API
    shareContent: async (data: { title: string; text: string; url?: string }) => {
        if (navigator.share) {
            try {
                await navigator.share(data);
                console.log('✅ Content shared successfully');
            } catch (error) {
                console.error('❌ Sharing failed:', error);
                // Fallback to clipboard
                fallbackShare(data);
            }
        } else {
            fallbackShare(data);
        }
    },

    // Add to home screen prompt
    showAddToHomeScreen: () => {
        // This will trigger the beforeinstallprompt event listener
        const event = new CustomEvent('show-install-prompt');
        window.dispatchEvent(event);
    },

    // Check PWA capabilities
    getPWACapabilities: () => {
        return {
            serviceWorker: 'serviceWorker' in navigator,
            webShare: 'share' in navigator,
            installPrompt: 'BeforeInstallPromptEvent' in window,
            badging: 'setAppBadge' in navigator,
        };
    },

    // Set app badge (for notification count)
    setAppBadge: (count: number = 0) => {
        if ('setAppBadge' in navigator) {
            (navigator as any).setAppBadge(count);
        }
    },

    // Clear app badge
    clearAppBadge: () => {
        if ('clearAppBadge' in navigator) {
            (navigator as any).clearAppBadge();
        }
    },
};

// Fallback share function
const fallbackShare = (data: { title: string; text: string; url?: string }) => {
    const shareText = `${data.title}\n${data.text}${data.url ? '\n' + data.url : ''}`;

    if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText).then(() => {
            console.log('✅ Content copied to clipboard');
            // Show toast notification
            showToast('Content copied to clipboard!');
        });
    } else {
        // Older browsers fallback
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showToast('Content copied to clipboard!');
    }
};

// Simple toast notification
const showToast = (message: string) => {
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #1e40af;
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    z-index: 10000;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  `;

    document.body.appendChild(toast);

    setTimeout(() => {
        toast.remove();
    }, 3000);
};

export default usePWA;
